import { useEffect, useState } from "react";
import { Edit2 } from "lucide-react";
import { getUserGroupsList } from "../../api/common";
import TableContainer from "../../Components/Table";
import Breadcrumb from "../../Components/Breadcrumb";
import AddEditRoleMappingModal from "../../Containers/UserManagement/AddEditRoleMappingModal";
import Alert from "../../Components/AlertPopup";
import { ERROR_MESSAGE } from "../../constants";
import PageContainer from "../PageContainer";
import styles from "./style.module.scss";

const breadcrumbItems = [
  { label: "Configuration", path: "#" },
  { label: "Administration", path: "#" },
  { label: "User Management", path: "/administration/usermanagement" },
  { label: "Role Mapping" },
];

const columnData = [
  { label: "S.No", key: "sno", disableSortBy: true },
  { label: "Role", key: "role" },
  { label: "AD Group Name", key: "adGroupName" },
  { label: "Updated By", key: "updatedBy" },
  { label: "Updated On", key: "updatedOn" },
  { label: "Action", key: "action", disableSortBy: true },
];

const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }) + " " + date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).toLowerCase();
};

const RoleMapping = () => {
  const [roleMappingData, setRoleMappingData] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editData, setEditData] = useState<any>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);

  useEffect(() => {
    fetchGroupsList();
  }, []);

  const fetchGroupsList = async () => {
    try {
      const response = await getUserGroupsList();
      if (response && Array.isArray(response)) {
        setRoleMappingData(response);
      }
    } catch {
      setShowErrorToast(true);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setEditData(null);
    setShowModal(true);
  };

  const openEditModal = (item: any) => {
    setModalMode("edit");
    setEditData({
      id: item.groupId,
      role: item.roleType,
      adGroupName: Array.isArray(item.groupNames) ? item.groupNames : [item.groupNames],
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData(null);
  };

  const handleSaveSuccess = () => {
    fetchGroupsList();
  };

  const rowData = roleMappingData.map((item, index) => ({
    sno: index + 1,
    role: item.roleType,
    adGroupName: Array.isArray(item.groupNames) ? item.groupNames.join(", ") : item.groupNames,
    updatedBy: item.updatedBy || "-",
    updatedOn: formatDate(item.updatedOn),
    action: (
      <Edit2
        size={16}
        className={styles.editIcon}
        onClick={() => openEditModal(item)}
      />
    ),
  }));

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (newLimit: number) => {
    setPageSize(newLimit);
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRows = rowData.slice(startIndex, startIndex + pageSize);

  return (
    <PageContainer>
      <div className={styles.breadcrumbNoNav}>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className={styles.container}>
        <TableContainer
          columnData={columnData}
          rowData={paginatedRows}
          title=""
          enableFilter={true}
          enableActionButton={false}
          itemsPerPage={pageSize}
          totalResult={rowData.length}
          currentPage={currentPage}
          onPageNoChange={handlePageChange}
          onPageLimitChange={handlePageSizeChange}
          showHeader={true}
        />
      </div>

      <AddEditRoleMappingModal
        show={showModal}
        onClose={handleModalClose}
        onSaveSuccess={handleSaveSuccess}
        mode={modalMode}
        data={editData}
      />

      <Alert
        type="error"
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={ERROR_MESSAGE.API_ERROR_MESSAGE}
      />
    </PageContainer>
  );
};

export default RoleMapping;